import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	useNavigate,
	useParams,
	useRouterState,
} from "@tanstack/react-router";
import {
	Calendar,
	CalendarCheck01,
	Hash01,
	Minimize01,
	Trash01,
} from "@untitledui/icons";
import { useCallback, useMemo, useState } from "react";
import { DeleteRecordModal } from "@/components/Organisms/DeleteRecordModal/DeleteRecordModal";
import {
	DetailsState,
	DetailsSurface,
} from "@/components/Organisms/Details/Details";
import {
	PageHeader,
	type PageHeaderTab,
} from "@/components/Organisms/PageHeader/PageHeader";
import { PageLayout } from "@/components/Organisms/PageLayout/PageLayout";
import { Sidebar } from "@/components/Organisms/Sidebar/Sidebar";
import {
	commitAutosaveOrStay,
	useJobDetailsSaveFailLabels,
} from "@/features/jobs/hooks/use-job-details-navigation-guard";
import {
	getAssetScopedRecordDrawerDetailsPath,
	getAssetScopedRecordWorkspacePath,
	getAssetWorkspacePath,
} from "@/features/jobs/lib/get-asset-workspace-path.util";
import {
	getRecordWorkspaceTabId,
	RecordWorkspaceTab,
	type RecordWorkspaceTabId,
} from "@/features/jobs/lib/get-record-workspace-tab-id.util";
import {
	RecordDetailsTab,
	RecordEntityDetails,
} from "@/features/records/components";
import {
	RecordFormSaveProvider,
	useRecordFormSaveRegistry,
} from "@/features/records/components/record-form-save-context";
import { useRecordFormNavigationGuard } from "@/features/records/hooks/use-record-form-navigation-guard";
import { getRecordDisplayTitle } from "@/features/records/lib/get-record-display-title.util";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { assetInstanceQueryOptions } from "@/services/asset";
import { authQueryOptions } from "@/services/auth";
import { jobQueryOptions } from "@/services/job";
import { recordInstanceQueryOptions } from "@/services/record";
import { cx } from "@/utils/cx";
import {
	createdMetadataLabel,
	updatedMetadataLabel,
} from "@/utils/entity-metadata-date-label";

/**
 * Nested Record workspace under Asset — breadcrumbs Jobs → Job # → Assets → Asset # → Records → Current.
 */
export const Route = createFileRoute(
	"/_authenticated/(web-app)/_web/web/$orgId/jobs/(job)/$jobId/workspace/assets/$assetId/workspace/records/$recordId/workspace",
)({
	component: AssetScopedRecordWorkspaceLayout,
});

function AssetScopedRecordWorkspaceLayout() {
	return (
		<RecordFormSaveProvider>
			<AssetScopedRecordWorkspaceLayoutInner />
		</RecordFormSaveProvider>
	);
}

function AssetScopedRecordWorkspaceLayoutInner() {
	const { t } = useAppTranslation();
	const navigate = useNavigate();
	const params = useParams({
		from: "/_authenticated/(web-app)/_web/web/$orgId/jobs/(job)/$jobId/workspace/assets/$assetId/workspace/records/$recordId/workspace",
	});
	const pathname = useRouterState({
		select: (state) => state.location.pathname,
	});
	const selectedTabId = getRecordWorkspaceTabId(pathname);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [isSidebarOpen, setIsSidebarOpen] = useState(true);

	const formSaveRegistry = useRecordFormSaveRegistry();
	const saveFailLabels = useJobDetailsSaveFailLabels();
	useRecordFormNavigationGuard();

	const runWithFormCommit = useCallback(
		async (action: () => void) => {
			const ctrl = formSaveRegistry?.getController() ?? null;
			if (ctrl?.hasPending()) {
				const shouldStay = await commitAutosaveOrStay(
					ctrl.commitPending,
					saveFailLabels,
				);
				if (shouldStay) return;
			}
			action();
		},
		[formSaveRegistry, saveFailLabels],
	);

	const recordQuery = useQuery(
		recordInstanceQueryOptions.detail(params.recordId, {
			organizationId: params.orgId,
		}),
	);

	const jobById = useQuery(
		jobQueryOptions.detail(params.jobId, {
			organizationId: params.orgId,
		}),
	);

	const assetQuery = useQuery(
		assetInstanceQueryOptions.detail(params.assetId, {
			organizationId: params.orgId,
		}),
	);

	const canDeleteRecord = useQuery({
		...authQueryOptions.checkAction({
			name: "DeleteRecord",
			entityType: "Record",
			entityId: params.recordId,
		}),
		enabled: !!params.recordId,
	});

	const title = getRecordDisplayTitle({
		name: recordQuery.data?.name,
		recordNumber: recordQuery.data?.recordNumber,
	});

	const jobNumberLabel =
		jobById.data?.jobNumber != null
			? t("main.jobs.workspace.records.workspace.breadcrumb.jobNumber", {
					jobNumber: jobById.data.jobNumber,
				})
			: t("main.jobs.workspace.title");

	const assetNumberLabel =
		assetQuery.data?.assetNumber != null
			? t("assets.breadcrumb.assetWithNumber", {
					defaultValue: "Asset #{{number}}",
					number: assetQuery.data.assetNumber,
				}) || `Asset #${assetQuery.data.assetNumber}`
			: t("main.jobs.workspace.tabs.assets", { defaultValue: "Assets" });

	const tabs: PageHeaderTab[] = useMemo(
		() => [
			{
				id: RecordWorkspaceTab.Form,
				label: t("jobs.landing.sections.form"),
			},
			{
				id: RecordWorkspaceTab.Assets,
				label: t("jobs.landing.sections.assets"),
			},
			{
				id: RecordWorkspaceTab.Attachments,
				label: t("jobs.landing.sections.attachments"),
			},
			{
				id: RecordWorkspaceTab.Notes,
				label: t("jobs.landing.sections.notes"),
			},
			{
				id: RecordWorkspaceTab.Map,
				label: t("jobs.landing.sections.map"),
			},
		],
		[t],
	);

	const metadata = useMemo(() => {
		const record = recordQuery.data;
		if (!record) return [];

		const items: {
			label: string;
			icon: typeof Hash01;
		}[] = [];

		if (record.recordNumber != null) {
			items.push({
				label: String(record.recordNumber),
				icon: Hash01,
			});
		}
		if (record.createdAt) {
			items.push({
				label: createdMetadataLabel(t, record.createdAt),
				icon: Calendar,
			});
		}
		if (record.updatedAt) {
			items.push({
				label: updatedMetadataLabel(t, record.updatedAt),
				icon: CalendarCheck01,
			});
		}
		return items;
	}, [recordQuery.data, t]);

	const handleBreadcrumbJobs = () => {
		void runWithFormCommit(() => {
			navigate({
				to: "/web/$orgId/jobs",
				params: { orgId: params.orgId },
			});
		});
	};

	const handleBreadcrumbJob = () => {
		void runWithFormCommit(() => {
			navigate({
				to: "/web/$orgId/jobs/$jobId/workspace/form",
				params: { orgId: params.orgId, jobId: params.jobId },
			});
		});
	};

	const handleBreadcrumbAssets = () => {
		void runWithFormCommit(() => {
			navigate({
				href: `/web/${params.orgId}/jobs/${params.jobId}/workspace/assets`,
			});
		});
	};

	const handleBreadcrumbAsset = () => {
		void runWithFormCommit(() => {
			navigate({
				href: getAssetWorkspacePath({
					orgId: params.orgId,
					jobId: params.jobId,
					assetId: params.assetId,
					tab: "form",
				}),
			});
		});
	};

	const handleBreadcrumbRecords = () => {
		void runWithFormCommit(() => {
			navigate({
				href: getAssetWorkspacePath({
					orgId: params.orgId,
					jobId: params.jobId,
					assetId: params.assetId,
					tab: "records",
				}),
			});
		});
	};

	const handleCollapseWorkspace = () => {
		void runWithFormCommit(() => {
			navigate({
				href: getAssetScopedRecordDrawerDetailsPath({
					orgId: params.orgId,
					jobId: params.jobId,
					assetId: params.assetId,
					recordId: params.recordId,
				}),
			});
		});
	};

	const handleTabChange = (tabId: string) => {
		const next = tabId as RecordWorkspaceTabId;
		if (next === selectedTabId) return;

		void runWithFormCommit(() => {
			navigate({
				href: getAssetScopedRecordWorkspacePath({
					orgId: params.orgId,
					jobId: params.jobId,
					assetId: params.assetId,
					recordId: params.recordId,
					tab: next,
				}),
			});
		});
	};

	const handleDeleteSuccess = () => {
		setIsDeleteModalOpen(false);
		void navigate({
			href: getAssetWorkspacePath({
				orgId: params.orgId,
				jobId: params.jobId,
				assetId: params.assetId,
				tab: "records",
			}),
			replace: true,
		});
	};

	const sidebarLabel = t("main.jobs.workspace.records.workspace.sidebarLabel");

	const sidebarTabs = useMemo(
		() => [
			{
				label: t("main.jobs.workspace.records.drawer.tabs.details.label"),
				id: RecordDetailsTab.Details,
			},
			{
				label: t("main.jobs.workspace.records.drawer.tabs.activity.label"),
				id: RecordDetailsTab.Activity,
			},
		],
		[t],
	);

	return (
		<div className="relative flex min-h-0 w-full flex-1 overflow-hidden">
			<div
				className={cx(
					"flex min-h-0 min-w-0 flex-1 flex-col transition-[padding] duration-300 ease-in-out motion-reduce:transition-none",
					isSidebarOpen && "lg:pr-[440px]",
				)}
			>
				<PageLayout
					header={
						<PageHeader
							type="record"
							title={title}
							breadcrumbType="multiple"
							breadcrumbs={[
								{
									label: t("main.jobs.workspace.backToJobs"),
									onPress: handleBreadcrumbJobs,
								},
								{
									label: jobNumberLabel,
									onPress: handleBreadcrumbJob,
								},
								{
									label: t("main.jobs.workspace.tabs.assets"),
									onPress: handleBreadcrumbAssets,
								},
								{
									label: assetNumberLabel,
									onPress: handleBreadcrumbAsset,
								},
								{
									label: t("main.jobs.workspace.tabs.records"),
									onPress: handleBreadcrumbRecords,
								},
								{
									label: t(
										"main.jobs.workspace.records.workspace.breadcrumb.current",
									),
									isCurrent: true,
								},
							]}
							metadata={metadata.length > 0 ? metadata : undefined}
							overflowAriaLabel={t("common.tableActions.options")}
							overflowActions={
								canDeleteRecord.data !== false
									? [
											{
												label: t(
													"main.jobs.workspace.records.drawer.delete.action",
												),
												icon: Trash01,
												onPress: () => setIsDeleteModalOpen(true),
											},
										]
									: []
							}
							actions={[
								{
									label: t("main.jobs.workspace.action"),
									"aria-label": t(
										"main.jobs.workspace.records.workspace.collapse",
									),
									icon: Minimize01,
									color: "secondary",
									onPress: handleCollapseWorkspace,
								},
							]}
							showTabs
							tabs={tabs}
							selectedTabId={selectedTabId}
							onTabChange={handleTabChange}
						/>
					}
				>
					<Outlet />
				</PageLayout>
			</div>
			<Sidebar
				isOpen={isSidebarOpen}
				onOpenChange={setIsSidebarOpen}
				aria-label={sidebarLabel}
				className="max-lg:hidden"
			>
				<RecordEntityDetails
					orgId={params.orgId}
					recordId={params.recordId}
					surface={DetailsSurface.Tertiary}
					state={
						recordQuery.isPending ? DetailsState.Loading : DetailsState.Default
					}
					tabs={sidebarTabs}
					data-testid="asset-scoped-record-workspace-sidebar-details"
				/>
			</Sidebar>
			<DeleteRecordModal
				isOpen={isDeleteModalOpen}
				setIsOpen={setIsDeleteModalOpen}
				handleClose={() => setIsDeleteModalOpen(false)}
				onDeleteSuccess={handleDeleteSuccess}
				recordId={params.recordId}
				organizationId={params.orgId}
			/>
		</div>
	);
}
